import "./PoolSelect.scss"
import React, { useState, useEffect, useMemo } from "react"
import { useBoolean } from "src/hooks/useBoolean"
import { Box } from "@mui/material"
import { POOLS_MAP, Pool, PoolName } from "src/constants"
import { MAX_POOL_SELECTABLE } from "src/constants/boostFarm"
import { Add, DeleteForeverOutlined } from "@mui/icons-material"
import RocketIcon from "src/assets/icons/rocket.svg"
import { getChainId } from "src/utils"
import { formatBoostedApr, formatBNToString } from "src/utils"
import usePoolData from "src/hooks/usePoolData"

import IncreaseBoostModal from "./IncreaseBoostModal"
import Text from "../Text"
import Button from "../Button"

interface PoolInitViewProps {
  onAdd: () => void
  onRemove: (poolName: PoolName) => void
  boostedPoolIds?: number[]
}

const PoolInitView: React.FC<PoolInitViewProps> = ({
  onAdd,
  onRemove,
  boostedPoolIds,
}) => {
  const chainId = getChainId()
  const [poolList, setPoolList] = useState<Pool[]>([])
  const availableCount = MAX_POOL_SELECTABLE - (boostedPoolIds?.length || 0)
  const availableList = useMemo(
    () => new Array<string>(availableCount).fill("0"),
    [availableCount],
  )

  useEffect(() => {
    setPoolList(
      Object.values(POOLS_MAP).filter(
        ({ addresses, rewardPids }) =>
          (chainId ? addresses[chainId] : false) &&
          boostedPoolIds?.includes(rewardPids[chainId]!),
      ),
    )
  }, [chainId, boostedPoolIds])

  const renderBoostedPools = useMemo(() => {
    return poolList.map((item) => {
      return (
        <BoostedPool key={item.name} poolName={item.name} onClick={onRemove} />
      )
    })
  }, [onRemove, poolList])

  const renderAddPools = useMemo(() => {
    return availableList.map((item, index) => {
      return <AddPool key={`add-${index}`} onClick={onAdd} />
    })
  }, [availableList, onAdd])

  return (
    <Box>
      <Box mb={3} className="divider" />
      <Text mb={3}>
        Select up to {MAX_POOL_SELECTABLE} pools to apply the Boost Power and
        enjoy higher APR. You can change the selection and boost a different
        pool later.
      </Text>
      <Text mb={2}>
        {boostedPoolIds?.length || 0} of {MAX_POOL_SELECTABLE} pools boosted
      </Text>
      <Box>
        {renderBoostedPools}
        {renderAddPools}
      </Box>
    </Box>
  )
}

export const BoostedPool: React.FC<{
  poolName: PoolName
  onClick: (poolName: PoolName) => void
}> = ({ poolName, onClick }) => {
  const pool = POOLS_MAP[poolName]
  const { lpToken } = pool
  const [poolData, , boostData] = usePoolData(poolName)
  const boostFactor = boostData?.boostFactor
    ? parseFloat(formatBNToString(boostData.boostFactor, 18))
    : 1
  const maxBoostFactor = boostData?.maxBoostFactor
    ? parseFloat(formatBNToString(boostData.maxBoostFactor, 18))
    : 1
  const [showModal, openModal, closeModal] = useBoolean(false)

  return (
    <Box
      mb={2}
      p="1px"
      sx={{
        position: "relative",
        borderRadius: "10px",
        background: "var(--outline-gradient)",
      }}
    >
      <Box
        padding="12px"
        sx={{
          borderRadius: "10px",
          backgroundColor: "#1A1A1A",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          my={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <img src={lpToken.icon} height={24} />
            <Text>{poolName}</Text>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} mr="12px">
            <img src={RocketIcon} height={12} />
            <Text bold size="small" color="#00A68C">
              Current APR{" "}
              {formatBoostedApr(poolData?.apr, poolData?.ferroApr, boostFactor)}
            </Text>
          </Box>
        </Box>
        <Box
          my="4px"
          padding="12px"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            borderRadius: "10px",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
        >
          <Box>
            <Text size="micro">
              Your boost can be up to{" "}
              {formatBoostedApr(
                poolData?.apr,
                poolData?.ferroApr,
                maxBoostFactor,
              )}
            </Text>
          </Box>
          <Button size="small" onClick={openModal}>
            Boost Further
          </Button>
        </Box>
        <Box my="4px">
          <Box
            display="flex"
            gap="4px"
            sx={{
              width: "fit-content",
              cursor: "pointer",
            }}
            onClick={() => {
              onClick(poolName)
            }}
          >
            <DeleteForeverOutlined fontSize="small" sx={{ color: "#009BE0" }} />
            <Text size="small" color="#009BE0">
              Remove This Boost
            </Text>
          </Box>
        </Box>
      </Box>
      <IncreaseBoostModal
        open={showModal}
        onClose={closeModal}
        poolName={poolName}
      />
    </Box>
  )
}

export const AddPool: React.FC<{
  onClick?: () => void
}> = ({ onClick }) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="126px"
      mb={2}
      sx={{
        border: "1px dashed #D8D8D8",
        borderRadius: "10px",
        cursor: "pointer",
      }}
      onClick={onClick}
    >
      <Box display="flex" gap="10px" alignItems="center">
        <Add sx={{ color: "#adadad" }} />
        <Text color="dim">Select Pool to boost</Text>
      </Box>
    </Box>
  )
}

export default PoolInitView
